import {
  TabProps,
  Tab as RACTab,
  composeRenderProps,
} from "react-aria-components";
import { motion } from "framer-motion";
import Icon from "../../foundations/Icons/Icon";
import { IconType } from "../../foundations/Icons/types";
import { tv } from "tailwind-variants";

interface Props extends TabProps {
  leftIcon: IconType;
  variant?: "default" | "fullwidth";
  children: React.ReactNode;
}

const styles = tv({
  slots: {
    base: "",
    icon: "",
  },
  variants: {
    variant: {
      default: {
        base: "text-md font-medium flex items-center bg-grey-1 h-10 rounded-2 px-6 cursor-pointer transition-colors outline-hidden",
        icon: "mr-2 bg-black",
      },
      fullwidth: {
        base: "relative cursor-pointer flex outline-hidden h-full md:min-w-64 md:grow-0 grow font-medium text-grey-8 text-center mr-1 items-center justify-center",
        icon: "mr-2 bg-grey-8",
      },
    },
  },
  compoundVariants: [
    {
      variant: "default",
      isHovered: true,
      className: "bg-grey-3",
    },
    {
      variant: "default",
      isFocused: true,
      className: "bg-grey-3",
    },
    {
      variant: "default",
      isSelected: true,
      className: {
        base: "bg-black text-white",
        icon: "bg-white",
      },
    },
    {
      variant: "fullwidth",
      isSelected: true,
      className: {
        base: "bg-clip-text text-transparent",
        icon: "bg-[#FF8762]",
      },
    },
  ],
});

export default function Tab({
  children,
  leftIcon,
  variant = "default",
  ...props
}: Props) {
  const { icon, base } = styles();
  return (
    <RACTab
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        base({
          ...renderProps,
          variant,
          className,
        }),
      )}
    >
      {(tab) => (
        <>
          {leftIcon && (
            <Icon
              dynamicColor
              icon={leftIcon}
              className={icon({ variant, ...tab })}
              size="sm"
            />
          )}
          {variant === "fullwidth" && tab.isSelected ? (
            <motion.span
              initial={{ backgroundPosition: "0 0" }}
              animate={{ backgroundPositionX: ["0px", "600px"] }}
              className="bg-clip-text text-transparent"
              transition={{
                duration: 6,
                ease: "linear",
                repeat: Infinity,
                repeatType: "loop",
              }}
              style={{
                backgroundImage: `linear-gradient(to right, #FF8762, #FFCC63, #FF7CBB, #FFCC63, #FF8762)`,
                backgroundSize: "600px 100%",
              }}
            >
              {children}
            </motion.span>
          ) : (
            children
          )}
          {variant === "fullwidth" && tab.isSelected && (
            <motion.span
              layoutId="underline"
              className="absolute bottom-0 left-0 right-0 h-1 rounded-1"
              initial={{ backgroundPosition: "0 0" }}
              animate={{ backgroundPositionX: ["0px", "600px"] }}
              transition={{
                type: "spring",
                mass: 0.2,
                backgroundPositionX: {
                  duration: 3,
                  ease: "linear",
                  repeat: Infinity,
                  repeatType: "loop",
                },
              }}
              style={{
                backgroundImage: `linear-gradient(to right, #FF8762, #FFCC63, #FF7CBB, #FFCC63, #FF8762)`,
                backgroundSize: "600px 100%",
              }}
            />
          )}
        </>
      )}
    </RACTab>
  );
}
